import type { GamePlayData, GameSessionPageData, InstantWinDraw } from '@/types'
import { InstantWinType } from '@/utils/helper-objects'

import { GameDemoSession } from './game-session-providers/game-session-demo'
import { GamePlaySession } from './game-session-providers/game-session-play'

/**
 * The number of tickets given to a player when in demo mode.
 */
const DEMO_SESSION_PLAY_COUNT = 3

/**
 * Service to handle game session operations
 */
export class GameSession {
  /**
   * Play a game session and get ticket results
   *
   * @param gameSessionId - The game session ID
   * @param playCount - Number of plays to make
   * @returns Promise with game play data (played entries, remaining plays, etc.)
   */
  static async playGame(
    gameSessionId: string,
    playCount: number = 1
  ): Promise<GamePlayData> {
    if (gameSessionId === GameSession.demoSessionIdentifier) {
      return GameDemoSession.playGame(playCount)
    }
    return GamePlaySession.playGame(gameSessionId, playCount)
  }

  /**
   * Get game session data and current state
   *
   * @param gameSessionId - The game session ID
   * @returns Promise with game session page data (game session, played entries, etc.)
   */
  static async getGameSession(
    gameSessionId: string
  ): Promise<GameSessionPageData> {
    if (gameSessionId === GameSession.demoSessionIdentifier) {
      return GameDemoSession.getSession()
    }
    return GamePlaySession.getSession(gameSessionId)
  }

  /**
   * Creates a new game session. Used for demo mode.
   *
   * @param gameSessionId The unqiue identifier for the game session
   * @param instantwin The InstantWin to create the game session for
   * @returns A Promise with the relavant session
   */
  static createDemoSession(instantwin: InstantWinDraw): GameSessionPageData {
    return GameDemoSession.createSession(instantwin, DEMO_SESSION_PLAY_COUNT)
  }

  /**
   * The shared session identifier for instant win game demos.
   */
  static get demoSessionIdentifier(): string {
    return GameDemoSession.demoSessionId
  }

  /**
   * Determines if a game supports demo modes or not.
   *
   * @param instantwin The instant win game to check
   * @returns `true` if the game type supports demo mode, otherwise `false`
   */
  static supportsDemoSession(instantwin: InstantWinDraw): boolean {
    return instantwin.instant_win_type_id !== InstantWinType.Standard
  }
}
